import React, { useEffect } from 'react';
import { Button, Form } from 'antd';
import { FormItem } from 'components/Base/Form';
import { AddItemModalProps } from './AddItemModal.types';
import { SInput, SModal } from './AddItemModal.styles';

export const AddItemModal: React.FC<AddItemModalProps> = ({
  visible,
  onCancel,
  onAdd,
  title = 'Add Item',
  fieldLabel = 'Name',
  placeholder = 'Enter Name',
  loading = false,
  validateInput,
  'data-cy': dataCy,
}) => {
  const [form] = Form.useForm();

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      onAdd(values.name);
      form.resetFields();
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  // Reset form when modal becomes visible
  useEffect(() => {
    if (visible) {
      form.resetFields();
    }
  }, [visible, form]);

  const validationRules = [
    {
      required: true,
      message: `Please enter ${fieldLabel.toLowerCase()}`,
    },
    {
      pattern: /^(?!\s*$).+/,
      message: `${fieldLabel} cannot be empty or only whitespace`,
    },
    ...(validateInput
      ? [
          {
            validator: (_: any, value: string) => {
              if (!value) return Promise.resolve();
              const errorMessage = validateInput(value);
              if (errorMessage) {
                return Promise.reject(new Error(errorMessage));
              }
              return Promise.resolve();
            },
          },
        ]
      : []),
  ];

  return (
    <SModal
      title={title}
      open={visible}
      onCancel={handleCancel}
      maskClosable={false}
      keyboard={false}
      getContainer={false}
      footer={[
        <Button
          key="cancel"
          onClick={handleCancel}
          data-cy={`${dataCy}-cancel-button`}
        >
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleOk}
          data-cy={`${dataCy}-add-button`}
        >
          Add
        </Button>,
      ]}
      width={400}
      data-cy={dataCy}
    >
      <Form form={form} layout="vertical" autoComplete="off">
        <FormItem name="name" label={fieldLabel} rules={validationRules}>
          <SInput placeholder={placeholder} data-cy={`${dataCy}-name-input`} />
        </FormItem>
      </Form>
    </SModal>
  );
};

export default AddItemModal;
